/**
 * EditarVeiculoPage — edicao de apelido, marca, modelo e cor de um veiculo.
 *
 * Placa NAO e editavel aqui (mudanca de placa exige anti-fraude e segue
 * pelo modal TrocarPlacaDialog em veiculos-page.tsx).
 *
 * Idioma visual espelha adicionar-veiculo-page.tsx (hero header com icone
 * gradient, inputs h-12 rounded-xl, swatches de cor, button accent).
 */
import { traduzirErroSupabase } from '@octawash/shared';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CarFront, CheckCircle2, Loader2, Palette, Sparkles, Tag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

// Cast — tab_marcas_veiculo / tab_modelos_veiculo nao estao em database.types ainda
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface Marca {
  seq_marca: number;
  nom_marca: string;
}
interface Modelo {
  seq_modelo: number;
  seq_marca: number;
  nom_modelo: string;
}
interface VeiculoEdit {
  seq_veiculo: number;
  seq_cliente: number;
  num_placa: string;
  cod_padrao_placa: string;
  nom_marca: string | null;
  nom_modelo: string | null;
  des_cor: string | null;
  cod_apelido: string | null;
}

const CORES_POPULARES: Array<{ nome: string; bg: string }> = [
  { nome: 'Branco', bg: '#FAFAFA' },
  { nome: 'Preto', bg: '#0F0F11' },
  { nome: 'Prata', bg: '#C8CDD2' },
  { nome: 'Cinza', bg: '#6B7280' },
  { nome: 'Vermelho', bg: '#DC2626' },
  { nome: 'Azul', bg: '#1D4ED8' },
  { nome: 'Verde', bg: '#15803D' },
  { nome: 'Marrom', bg: '#78350F' },
  { nome: 'Bege', bg: '#D6CCB0' },
  { nome: 'Amarelo', bg: '#EAB308' },
];

export function EditarVeiculoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seqVeiculo } = useParams<{ seqVeiculo: string }>();
  const seqVeiculoNum = Number(seqVeiculo);

  // Carrega veiculo + lista de marcas em paralelo
  const veiculo = useQuery({
    queryKey: ['veiculo-edit', seqVeiculoNum, user?.id],
    queryFn: async (): Promise<VeiculoEdit | null> => {
      if (!seqVeiculoNum || !user) return null;
      // RLS garante que so retorna se for do cliente atual
      const { data, error } = await supabase
        .from('tab_veiculos')
        .select(
          'seq_veiculo, seq_cliente, num_placa, cod_padrao_placa, nom_marca, nom_modelo, des_cor, cod_apelido',
        )
        .eq('seq_veiculo', seqVeiculoNum)
        .maybeSingle();
      if (error) throw error;
      return data as VeiculoEdit | null;
    },
    enabled: !!user && !!seqVeiculoNum,
  });

  const marcas = useQuery({
    queryKey: ['marcas-veiculo'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('tab_marcas_veiculo')
        .select('seq_marca, nom_marca')
        .eq('ind_ativo', true)
        .order('num_ordem')
        .order('nom_marca');
      if (error) throw error;
      return (data ?? []) as Marca[];
    },
    staleTime: 60 * 60 * 1000,
  });

  // Form state — preenche apos carregar veiculo
  const [seqMarca, setSeqMarca] = useState<string>('');
  const [seqModelo, setSeqModelo] = useState<string>('');
  const [cor, setCor] = useState('');
  const [apelido, setApelido] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erroDetalhado, setErroDetalhado] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  // Quando o veiculo carrega, resolve seq_marca pelo nom_marca atual
  useEffect(() => {
    if (!veiculo.data || !marcas.data) return;
    const m = marcas.data.find((x) => x.nom_marca === veiculo.data!.nom_marca);
    if (m) setSeqMarca(String(m.seq_marca));
    setCor(veiculo.data.des_cor ?? '');
    setApelido(veiculo.data.cod_apelido ?? '');
  }, [veiculo.data, marcas.data]);

  // Modelos filtrados pela marca selecionada
  const modelos = useQuery({
    queryKey: ['modelos-veiculo', seqMarca],
    queryFn: async () => {
      if (!seqMarca) return [] as Modelo[];
      const { data, error } = await sb
        .from('tab_modelos_veiculo')
        .select('seq_modelo, seq_marca, nom_modelo')
        .eq('seq_marca', Number(seqMarca))
        .eq('ind_ativo', true)
        .order('nom_modelo');
      if (error) throw error;
      return (data ?? []) as Modelo[];
    },
    enabled: !!seqMarca,
    staleTime: 60 * 60 * 1000,
  });

  // Resolve seq_modelo apos modelos carregarem
  useEffect(() => {
    if (!veiculo.data || !modelos.data || !seqMarca) return;
    const m = modelos.data.find((x) => x.nom_modelo === veiculo.data!.nom_modelo);
    if (m) setSeqModelo(String(m.seq_modelo));
  }, [veiculo.data, modelos.data, seqMarca]);

  const marcaSelecionada = useMemo(
    () => marcas.data?.find((m) => String(m.seq_marca) === seqMarca),
    [marcas.data, seqMarca],
  );
  const modeloSelecionado = useMemo(
    () => modelos.data?.find((m) => String(m.seq_modelo) === seqModelo),
    [modelos.data, seqModelo],
  );

  const formValido = !!(marcaSelecionada && modeloSelecionado && cor.trim().length > 0);

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    setErroDetalhado(null);
    if (!formValido || !veiculo.data) {
      toast.error('Preencha todos os campos obrigatorios');
      return;
    }
    setCarregando(true);
    try {
      const { error } = await supabase
        .from('tab_veiculos')
        .update({
          nom_marca: marcaSelecionada!.nom_marca,
          nom_modelo: modeloSelecionado!.nom_modelo,
          des_cor: cor.trim(),
          cod_apelido: apelido.trim() || null,
        })
        .eq('seq_veiculo', veiculo.data.seq_veiculo);
      if (error) throw error;

      toast.success('Veiculo atualizado!', { description: 'Suas alteracoes foram salvas.' });
      setSucesso(true);
      setTimeout(() => navigate('/veiculos'), 1400);
    } catch (err) {
      const msg = traduzirErroSupabase(err);
      setErroDetalhado(msg);
      toast.error('Falha ao salvar', { description: msg });
      setCarregando(false);
    }
  }

  // ---------------------------------------------------------------------------
  // LOADING (skeleton arejado)
  // ---------------------------------------------------------------------------
  if (veiculo.isLoading || marcas.isLoading) {
    return (
      <div className="space-y-5 p-4 pb-24">
        <Skeleton className="h-14 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  if (!veiculo.data) {
    return (
      <div className="space-y-4 p-6 pb-24 text-center">
        <p className="text-muted-foreground">Veiculo nao encontrado ou sem acesso.</p>
        <Button variant="outline" onClick={() => navigate('/veiculos')} className="rounded-xl">
          Voltar pra meus veiculos
        </Button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // TELA CELEBRATORIA
  // ---------------------------------------------------------------------------
  if (sucesso) {
    return (
      <div className="bg-octa-hero relative flex min-h-[80vh] items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div className="bg-octa-gradient shadow-octa-glow mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/40">
            <CheckCircle2 className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-display-lg mt-6 font-extrabold tracking-tight">Salvo!</h1>
          <p className="text-muted-foreground mt-3 text-sm">
            <span className="font-bold tracking-wider">{veiculo.data.num_placa}</span>
            <br />
            atualizado com sucesso.
          </p>
        </motion.div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // FORM
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Page header com back + icone gradient */}
      <div className="octa-fade-in-up flex items-start gap-3 pt-1">
        <button
          type="button"
          onClick={() => navigate(`/veiculos/${veiculo.data!.seq_veiculo}`)}
          aria-label="Voltar"
          className="octa-tap text-muted-foreground hover:text-foreground border-border/40 bg-card/60 hover:bg-accent/60 mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border backdrop-blur-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 space-y-1">
          <p className="text-overline text-muted-foreground uppercase">Sua frota</p>
          <h1 className="text-display-md font-extrabold tracking-tight">Editar veiculo</h1>
          <p className="text-muted-foreground text-sm">
            Atualize apelido, modelo ou cor. A placa segue por um fluxo proprio.
          </p>
        </div>
        <div className="bg-octa-gradient shadow-octa-glow flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white">
          <CarFront className="h-6 w-6" />
        </div>
      </div>

      {/* Placa imutavel — card informativo */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.4 }}
      >
        <Card variant="glass" className="overflow-hidden">
          <CardContent className="space-y-2 p-5 text-center">
            <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.18em]">
              Placa
            </p>
            <p className="text-display-sm font-extrabold tracking-[0.2em]">
              {veiculo.data.num_placa}
            </p>
            <p className="text-muted-foreground text-xs">
              Pra trocar a placa, use a opcao Mudar placa na tela de detalhe.
            </p>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.5 }}
      >
        <Card variant="default">
          <CardContent className="p-5">
            <form onSubmit={salvar} className="space-y-5">
              {/* Marca + Modelo (cascata) */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Marca <span className="text-rose-500">*</span>
                  </Label>
                  <Select
                    value={seqMarca}
                    onValueChange={(v) => {
                      setSeqMarca(v);
                      setSeqModelo('');
                    }}
                  >
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {marcas.data?.map((m) => (
                        <SelectItem key={m.seq_marca} value={String(m.seq_marca)}>
                          {m.nom_marca}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Modelo <span className="text-rose-500">*</span>
                  </Label>
                  <Select
                    value={seqModelo}
                    onValueChange={setSeqModelo}
                    disabled={!seqMarca || modelos.isLoading}
                  >
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue
                        placeholder={
                          !seqMarca
                            ? 'Marca primeiro'
                            : modelos.isLoading
                              ? 'Carregando...'
                              : 'Selecione'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {modelos.data?.map((m) => (
                        <SelectItem key={m.seq_modelo} value={String(m.seq_modelo)}>
                          {m.nom_modelo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Cor — swatches visuais + input livre */}
              <div className="space-y-2">
                <Label htmlFor="cor" className="text-xs font-semibold uppercase tracking-wider">
                  <Palette className="mr-1 inline h-3 w-3" />
                  Cor <span className="text-rose-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {CORES_POPULARES.map((c) => {
                    const ativo = cor === c.nome;
                    return (
                      <button
                        key={c.nome}
                        type="button"
                        onClick={() => setCor(c.nome)}
                        aria-pressed={ativo}
                        className={cn(
                          'octa-tap border-border/40 bg-card/40 group relative flex h-9 items-center gap-2 rounded-full border pl-1 pr-3 text-xs font-semibold transition-all',
                          ativo
                            ? 'border-primary shadow-octa-glow scale-105'
                            : 'hover:border-primary/40 hover:bg-accent/60',
                        )}
                      >
                        <span
                          className="ring-border/60 h-7 w-7 rounded-full ring-1"
                          style={{ background: c.bg }}
                          aria-hidden
                        />
                        {c.nome}
                        {ativo && <CheckCircle2 className="text-primary h-3.5 w-3.5" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
                <Input
                  id="cor"
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                  placeholder="ou digite outra cor"
                  className="h-11 rounded-xl"
                />
              </div>

              {/* Apelido */}
              <div className="space-y-1.5">
                <Label htmlFor="apelido" className="text-xs font-semibold uppercase tracking-wider">
                  <Tag className="mr-1 inline h-3 w-3" />
                  Apelido{' '}
                  <span className="text-muted-foreground text-[10px] normal-case tracking-normal">
                    (opcional)
                  </span>
                </Label>
                <Input
                  id="apelido"
                  value={apelido}
                  onChange={(e) => setApelido(e.target.value)}
                  placeholder="Carro do trabalho, Carro da esposa..."
                  maxLength={40}
                  className="h-11 rounded-xl"
                />
              </div>

              {/* Erro detalhado */}
              <AnimatePresence>
                {erroDetalhado && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
                      <p className="font-semibold">Nao foi possivel salvar</p>
                      <p className="mt-1 text-xs">{erroDetalhado}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Acoes */}
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/veiculos/${veiculo.data!.seq_veiculo}`)}
                  disabled={carregando}
                  className="h-12 rounded-xl sm:flex-none"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  disabled={carregando || !formValido}
                  className="h-12 flex-1 rounded-xl text-base font-bold"
                >
                  {carregando ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Salvar alteracoes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
